/**
 * RoleChangeDialog component
 * Confirmation dialog for role changes with reason field
 */
import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RoleBadge } from './RoleBadge'
import type { RoleCode } from '../../types/admin'

export interface RoleChangeDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** User's display name */
  userName: string
  /** User's email address */
  userEmail: string
  /** Current role before change */
  currentRole: RoleCode
  /** New role after change */
  newRole: RoleCode
  /** Called when change is confirmed with optional reason */
  onConfirm: (reason: string) => void
  /** Whether the confirmation is pending */
  isPending?: boolean
}

/**
 * Confirmation dialog displayed when changing a user's role
 */
export function RoleChangeDialog({
  open,
  onOpenChange,
  userName,
  userEmail,
  currentRole,
  newRole,
  onConfirm,
  isPending = false,
}: RoleChangeDialogProps) {
  const [reason, setReason] = useState('')

  const handleConfirm = () => {
    onConfirm(reason)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Confirm Role Change
          </DialogTitle>
          <DialogDescription>
            You are about to change the role for:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User info */}
          <div className="text-center">
            <p className="font-medium">{userName}</p>
            <p className="text-sm text-muted-foreground">{userEmail}</p>
          </div>

          {/* Role transition */}
          <div className="flex items-center justify-center gap-3">
            <RoleBadge role={currentRole} />
            <span className="text-muted-foreground">→</span>
            <RoleBadge role={newRole} />
          </div>

          {/* Reason field */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Textarea
              id="reason"
              placeholder="Enter a reason for this change..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isPending}
              rows={3}
            />
          </div>

          {/* Audit notice */}
          <p className="text-xs text-muted-foreground text-center">
            This action will be recorded in the audit log.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'Confirming...' : 'Confirm Change'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
